/**
 * Loop subscription widget shows Shopify ex-VAT selling-plan prices.
 * Retail catalogue / PDP hero uses ×1.2 for display — mirror that in Loop
 * numeric price nodes only (no "inc VAT" copy).
 *
 * Loop re-renders price text when toggling One-time ↔ Subscribe & Save.
 * We must re-apply ×1.2 after those updates (stale marks must not block).
 *
 * Prefer fixing tax display in Shopify Markets / Loop admin when available;
 * this is a storefront display bridge matching theme price.liquid.
 */
(function () {
  const VAT_MULTIPLIER = 1.2;
  const MARK = 'data-viperia-vat-adjusted';
  const LAST = 'data-viperia-vat-last';
  const MONEY_RE =
    /([£$€])\s*(\d{1,3}(?:,\d{3})*\.\d{2}|\d+\.\d{2})|((?:USD|GBP|EUR)\s+)(\d{1,3}(?:,\d{3})*\.\d{2}|\d+\.\d{2})/g;

  const ROOT_SELECTORS = [
    '#loop-widget-container',
    '[id*="loop-widget"]',
    '[id*="loop_widget"]',
    '[id*="LoopWidget"]',
    '[class*="loop-widget"]',
    '[class*="loop_widget"]',
    '[class*="LoopWidget"]',
    '[data-loop-widget]',
    'loop-subscription-widget',
    '[id*="loop-subscriptions"]',
    '[class*="loop-subscriptions"]',
  ].join(',');

  const DISCLAIMER_SELECTOR =
    '[data-viperia-vat-disclaimer], .product__info-container .product__tax, .product__tax';

  let disclaimerSnapshot = null;

  function isWholesale() {
    return (
      document.documentElement.classList.contains('viperia-wholesale') ||
      document.body.classList.contains('viperia-wholesale') ||
      document.body.dataset.customerWholesale === 'true'
    );
  }

  function formatAmount(amount, sample) {
    const fixed = amount.toFixed(2);
    const withCommas = sample.includes(',')
      ? fixed.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      : fixed;

    if (sample.includes('£')) return '£' + withCommas;
    if (sample.includes('$')) return '$' + withCommas;
    if (sample.includes('€')) return '€' + withCommas;
    if (/GBP/i.test(sample)) return sample.replace(/[\d,]+\.\d{2}/, withCommas);
    if (/USD/i.test(sample)) return sample.replace(/[\d,]+\.\d{2}/, withCommas);
    if (/EUR/i.test(sample)) return sample.replace(/[\d,]+\.\d{2}/, withCommas);
    return withCommas;
  }

  function adjustMoneyString(text) {
    if (!text || text.indexOf('.') === -1) return null;
    if (!/[£$€]|\b(?:USD|GBP|EUR)\b/.test(text)) return null;

    MONEY_RE.lastIndex = 0;
    let changed = false;
    const next = text.replace(MONEY_RE, (match) => {
      const numberMatch = match.match(/(\d{1,3}(?:,\d{3})*\.\d{2}|\d+\.\d{2})/);
      if (!numberMatch) return match;
      const value = parseFloat(numberMatch[1].replace(/,/g, ''));
      if (!Number.isFinite(value) || value <= 0) return match;

      const gross = Math.round(value * VAT_MULTIPLIER * 100) / 100;
      changed = true;
      return formatAmount(gross, match);
    });

    return changed ? next : null;
  }

  function normalizeMoneyText(text) {
    return (text || '').replace(/\s+/g, ' ').trim();
  }

  function walk(root) {
    if (!root || root.nodeType !== 1) return;

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!node.nodeValue || !/\d/.test(node.nodeValue)) return NodeFilter.FILTER_REJECT;
        if (!node.parentElement) return NodeFilter.FILTER_REJECT;
        const tag = node.parentElement.tagName;
        if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'NOSCRIPT') {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    nodes.forEach((textNode) => {
      const parent = textNode.parentElement;
      if (!parent) return;

      const current = normalizeMoneyText(textNode.nodeValue);
      const last = parent.getAttribute(LAST);

      // Already showing our last adjusted value — do not ×1.2 again.
      if (last && current === normalizeMoneyText(last)) return;

      // Loop (or first paint) wrote a new ex-VAT string — adjust from current text.
      const adjusted = adjustMoneyString(textNode.nodeValue);
      if (!adjusted) return;

      textNode.nodeValue = adjusted;
      parent.setAttribute(MARK, '1');
      parent.setAttribute(LAST, normalizeMoneyText(adjusted));
    });
  }

  function isLoopBlock(el) {
    if (!el) return false;
    const hay = ((el.id || '') + ' ' + (el.className || '') + ' ' + (el.getAttribute('type') || '')).toLowerCase();
    return hay.includes('loop');
  }

  function isInsideLoop(el) {
    if (!el || !el.closest) return false;
    try {
      if (el.closest(ROOT_SELECTORS)) return true;
    } catch (e) {
      // ignore
    }
    const block = el.closest('.shopify-app-block, .shopify-block');
    return isLoopBlock(block);
  }

  function findRoots() {
    const roots = new Set();
    try {
      document.querySelectorAll(ROOT_SELECTORS).forEach((el) => roots.add(el));
    } catch (e) {
      // ignore invalid selector edge cases
    }

    document.querySelectorAll('.shopify-app-block, .shopify-block').forEach((el) => {
      if (isLoopBlock(el)) roots.add(el);
    });

    return Array.from(roots);
  }

  function captureDisclaimer() {
    const el = document.querySelector(DISCLAIMER_SELECTOR);
    if (!el) return;
    el.setAttribute('data-viperia-vat-disclaimer', '1');
    if (!disclaimerSnapshot) {
      disclaimerSnapshot = {
        html: el.outerHTML,
        parent: el.parentNode,
        afterPrice: el.previousElementSibling,
      };
    }
  }

  function ensureVatDisclaimer() {
    captureDisclaimer();

    let el = document.querySelector(DISCLAIMER_SELECTOR);
    if (el) {
      el.hidden = false;
      el.removeAttribute('hidden');
      el.classList.remove('hidden');
      if (el.style.display === 'none') el.style.removeProperty('display');
      if (el.style.visibility === 'hidden') el.style.removeProperty('visibility');
      return;
    }

    // Loop sometimes re-renders siblings and strips the theme tax note — restore it.
    if (!disclaimerSnapshot || !disclaimerSnapshot.html) return;
    const parent = disclaimerSnapshot.parent;
    if (!parent || !parent.isConnected) return;

    const tmp = document.createElement('div');
    tmp.innerHTML = disclaimerSnapshot.html;
    const node = tmp.firstElementChild;
    if (!node) return;

    const price =
      parent.querySelector('[id^="price-"]') ||
      (disclaimerSnapshot.afterPrice && disclaimerSnapshot.afterPrice.isConnected
        ? disclaimerSnapshot.afterPrice
        : null);

    if (price && price.parentNode === parent) {
      price.insertAdjacentElement('afterend', node);
    } else {
      parent.appendChild(node);
    }
  }

  function run() {
    if (isWholesale()) return;
    ensureVatDisclaimer();
    findRoots().forEach(walk);
  }

  let scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      run();
    });
  }

  function init() {
    run();
    [200, 600, 1200, 2500, 5000].forEach((ms) => setTimeout(run, ms));

    const observer = new MutationObserver(schedule);
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // Loop plan toggles re-render prices shortly after click/change.
    function onLoopInteract(event) {
      if (!isInsideLoop(event.target)) return;
      schedule();
      setTimeout(run, 50);
      setTimeout(run, 200);
      setTimeout(run, 500);
    }

    document.addEventListener('change', onLoopInteract, true);
    document.addEventListener('click', onLoopInteract, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
